<template>
  <PsModal
    v-model:show="curValue"
    header="История материала"
    size="lg"
  >
    <div class="relations-news-modal">
      <CircularLoader
        v-if="isLoading"
        class="relations-news-modal__loader"
      />
      <div
        v-else
        class="relations-news-modal__container"
      >
        <div class="relations-news-modal__col">
          <RelationsEntry
            v-for="(rel, index) in leftItems"
            :key="index"
            type="newsItem"
            :entry="rel.entry"
            :hasHorizontalLine="rel.hasHorizontalLine"
            :isFlash="rel.entry?.type_id === ENewsTypeReverse.FLASH"
            :isExpress="rel.entry?.type_id === ENewsTypeReverse.EXPRESS"
            @click="clickHandler"
          />
        </div>
        <div class="relations-news-modal__col">
          <RelationsEntry
            v-for="(rel, index) in centerItems"
            :key="index"
            :type="rel.type"
            :entry="rel.entry"
            :isCurrent="rel.isCurrent"
            :hasHorizontalLine="rel.hasHorizontalLine"
            :isFlash="rel.entry?.type_id === ENewsTypeReverse.FLASH"
            :isExpress="rel.entry?.type_id === ENewsTypeReverse.EXPRESS"
            @click="clickHandler"
          />
        </div>
        <div class="relations-news-modal__col">
          <RelationsEntry
            v-for="(rel, index) in rightItems"
            :key="index"
            :type="rel.type"
            :entry="rel.entry"
            :hasHorizontalLine="false"
            :isFlash="rel.entry?.type_id === ENewsTypeReverse.FLASH"
            :isExpress="rel.entry?.type_id === ENewsTypeReverse.EXPRESS"
            @click="clickHandler"
          />
        </div>
      </div>
    </div>
  </PsModal>
</template>

<script setup lang="ts">
import { useAsyncState } from '@vueuse/core'
import { computed, inject } from 'vue'
import DOMPurify from 'dompurify'
import { noop } from 'lodash-es'
import CircularLoader from '@/ContextTab/components/Busy/Loader/Circular.vue'
import RelationsEntry from '@/ContextTab/components/RelationsEntry.vue'
import PsModal from '@/ContextTab/components/UI/PsModal.vue'
import { formatUsername } from '@/utils/formatUsername'
import { ENewsLinkReverse, ENewsStatusReverse, ENewsTypeReverse } from '@/lib/references'
import { apiClientV1, apiClientV2 } from '@/ContextTab/services/apiClient.ts'
import { NotificationHandlerKey } from '@/injectionKeys.ts'
import type { EntryItem, RelationsEntryItem }
  from '@/ContextTab/modules/news/published/components/types.ts'
import type {
  ChildLinksOfDataOfNewsRelationsGetOutReadable,
  ParentLinksOfDataOfNewsRelationsGetOutReadable,
} from '@/api/generated/v1'
import type { NewsVersionDetailed } from '@/api/generated/v2'


const curValue = defineModel<boolean>({ required: true })

const props = defineProps<{
  itemId: number // <-- both used for editor and published, so re-fetch item
}>()

const emit = defineEmits<{
  (e: 'open', id: number): void
}>()

const { onError } = inject(NotificationHandlerKey, { onError: noop })

const {
  state: newsRelations,
  isLoading,
} = useAsyncState(
  async () => {
    const news_id = props.itemId
    const result = await Promise.all([
      apiClientV1.NewsService.getNewsByNewsIdRelations({
        path: {
          news_id,
        },
      }),
      apiClientV2.NewsService.newsNewsNewsIdGet({
        path: {
          news_id,
        },
      }),
    ])

    if (result[0].error || result[1].error) {
      throw new Error('Не удалось загрузить связи новости')
    }

    return {
      relations: result[0].data.data,
      item: result[1].data,
    }
  },
  undefined,
  { onError },
)

const newsbreak = computed(() => newsRelations.value?.relations?.newsbreak)
const asoiNote = computed(() => newsRelations.value?.relations?.asoi_note)

const outgoingAsoiNotes = computed(() => newsRelations.value?.relations?.outgoing_asoi_notes || [])

// TODO: Некорректный тип, поэтому используем каст типов, при переходе на v2 API можно будет убрать каст типов
const childLinks = computed<ChildLinksOfDataOfNewsRelationsGetOutReadable[]>(() =>
  (newsRelations.value?.relations?.child_links as unknown as ChildLinksOfDataOfNewsRelationsGetOutReadable[]) || [],
)

// TODO: Некорректный тип, поэтому используем каст типов, при переходе на v2 API можно будет убрать каст типов
const parentLinks = computed<ParentLinksOfDataOfNewsRelationsGetOutReadable[]>(() =>
  (newsRelations.value?.relations?.parent_links as unknown as ParentLinksOfDataOfNewsRelationsGetOutReadable[]) || [],
)

const validLinkTypes: number[] = [
  ENewsLinkReverse.REPUBLICATION,
  ENewsLinkReverse.REPUBLICATION_UNEDITED,
  ENewsLinkReverse.REVOCATION,
  ENewsLinkReverse.EXTENSION,
]

const leftItems = computed(() => {
  const items: any[] = []
  const parentNews = parentLinks.value
    .filter((link) => validLinkTypes.includes(link.type_id))
    .map((link) => ({ type: 'newsItem', entry: link.news }))

  // Инфоповод
  if (newsbreak.value) {
    items.push({
      type: 'newsbreak',
      entry: newsbreak.value,
      hasHorizontalLine: !(asoiNote.value || parentNews.length > 0),
    })
  }

  // Заметка АСОИ
  if (asoiNote.value) {
    items.push({
      type: 'asoiNote',
      entry: asoiNote.value,
    })
  }

  Array.prototype.push.apply(items, parentNews)

  return items.map((item) => prepareItem(item))
})

const centerItems = computed(() => {
  if (!newsRelations.value?.item) {
    return []
  }

  const currentNewsItem = prepareItem({
    type: 'newsItem',
    entry: newsRelations.value?.item,
    isCurrent: true,
    hasHorizontalLine: rightItems.value.length > 0,
  })

  const items = [currentNewsItem]

  if (leftItems.value.length > 1) {
    items.unshift({ type: 'thumb' })
  }

  return items
})

const rightItems = computed(() => {
  const items = []
  const childNews = childLinks.value
    .filter((link) => validLinkTypes.includes(link.type_id))
    .map((link) => ({ type: 'newsItem', entry: link.news }))

  Array.prototype.push.apply(items, childNews)

  // Исходящая заметка АСОИ
  if (outgoingAsoiNotes.value) {
    const notes = outgoingAsoiNotes.value.map((note) => {
      return { type: 'asoiNote', entry: note }
    })
    Array.prototype.push.apply(items, notes)
  }

  return items.map((item) => prepareItem(item))
})


function prepareItem(item): RelationsEntryItem {
  const entry: EntryItem = {
    id: item.entry.id,
    type: item.type,
    meta: prepareMeta(item),
  }

  switch (item.type) {
    case 'newsItem': {
      entry.type_id = item.entry.version.type_id
      entry.date = item.entry.status_modified_at
      entry.body = prepareNewsItemText(item.entry.version)
      entry.is_revoked = item.entry.is_revoked
      break
    }

    case 'asoiNote':
      entry.type_id = item.entry.type_id
      entry.date = item.entry.created_at
      entry.body = prepareNewsItemText(item.entry) || 'Пустая заметка'
      break

    case 'newsbreak': {
      entry.source = item.entry.lead.source.name
      entry.date = item.entry.created_at
      entry.body = item.entry.lead.clearedText
      break
    }

    default:
      return item
  }

  return {
    type: item.type,
    entry,
    isCurrent: item.isCurrent,
    hasHorizontalLine: item.hasHorizontalLine,
  }
}

function prepareMeta(item): string {
  switch (item.type) {
    case 'newsItem': {
      const statusIds = [ENewsStatusReverse.WAITS_FOR_PUBLICATION, ENewsStatusReverse.PUBLISHED]
      const authorsList = prepareAuthors(item.entry.version)
      let authorsString = authorsList.join(', ')
      if (
        statusIds.includes(item.entry.version.status_id)
        && item.entry.publisher
      ) {
        authorsString = item.entry.publisher.name + ' / ' + authorsString
      } else {
        authorsString
          = (authorsList.length > 1
            ? 'Авторы: '
            : 'Автор: ') + authorsString
      }

      return authorsString
    }

    case 'asoiNote': {
      if (item.entry.recipient_name) {
        return 'Кому: ' + item.entry.recipient_name
      }

      if (item.entry.sender) {
        return 'Отправитель: ' + item.entry.sender.name
      }
      return ''
    }

    default:
      return ''
  }
}

function prepareNewsItemText(version: NewsVersionDetailed) {
  if (version.headline) {
    return DOMPurify.sanitize(version.headline, { ALLOWED_TAGS: ['p'] }).slice(0, 140)
  }

  if (version.body) {
    return DOMPurify.sanitize(version.body, { ALLOWED_TAGS: ['p'] }).slice(0, 120)
  }
  return 'Пустая новость'
}

function prepareAuthors(version: NewsVersionDetailed) {
  return (
    version?.authors?.map(({ user }) => formatUsername(user.name)) ?? []
  )
}

function clickHandler(entry) {
  emit('open', entry.id)
}
</script>

<style scoped>
.relations-news-modal__loader {
  margin: 4rem auto;
  width: 100%;
}

.relations-news-modal__container {
  display: flex;
  padding: 25px 0;
}

.relations-news-modal__col {
  flex: 0 0 calc(100% / 3);
  max-width: calc(100% / 3);

  &:first-child {
    padding-left: 15px;
  }
  &:last-child {
    padding-right: 15px;
  }
}
</style>
